import { z } from 'zod';
import { PROJECT_STATUSES, projectSummarySchema } from './organizations';

/** As quatro perspectivas padrão do BSC (PRD §7), criadas com cada projeto, com peso igual. */
export const DEFAULT_PERSPECTIVES = [
  {
    name: 'Financeira',
    description: 'Resultados financeiros que a estratégia deve gerar para os acionistas.',
  },
  {
    name: 'Cliente',
    description: 'Como a empresa quer ser vista pelos clientes e o valor que entrega a eles.',
  },
  {
    name: 'Processos',
    description: 'Processos em que a empresa precisa ser excelente para cumprir a estratégia.',
  },
  {
    name: 'Aprendizado e Desenvolvimento Organizacional',
    description: 'Pessoas, sistemas e cultura que sustentam os processos e o crescimento.',
  },
] as const;

export const perspectiveSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  /** Peso na performance da empresa, em % (0–100); null = sem peso. */
  weight: z.number().nullable(),
  position: z.number().int(),
  archivedAt: z.string().nullable(),
});
export type Perspective = z.infer<typeof perspectiveSchema>;
export const perspectiveListSchema = z.object({ items: z.array(perspectiveSchema) });

const perspectiveName = z
  .string()
  .trim()
  .min(2, 'Informe o nome da perspectiva.')
  .max(80, 'Nome longo demais.');
const perspectiveDescription = z.string().trim().max(500, 'A descrição pode ter no máximo 500 caracteres.');
const perspectiveWeight = z.coerce
  .number({ message: 'Informe o peso em números.' })
  .min(0, 'O peso vai de 0 a 100.')
  .max(100, 'O peso vai de 0 a 100.')
  .multipleOf(0.01, 'Use no máximo duas casas decimais.');

/** Nova perspectiva: descrição e peso opcionais (vazio = sem descrição / sem peso). */
export const perspectiveInputSchema = z.object({
  name: perspectiveName,
  description: perspectiveDescription
    .nullable()
    .optional()
    .transform((v) => v || null),
  weight: perspectiveWeight
    .nullable()
    .optional()
    .transform((v) => v ?? null),
});
/**
 * Alteração parcial: só muda o que veio. `null` (ou descrição vazia) limpa o campo; campo ausente
 * fica como está.
 */
export const perspectivePatchSchema = z
  .object({
    name: perspectiveName.optional(),
    description: perspectiveDescription
      .nullable()
      .optional()
      .transform((v) => (v === undefined ? undefined : v || null)),
    weight: perspectiveWeight.nullable().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Informe ao menos um campo para alterar.');
export type PerspectiveInput = z.input<typeof perspectiveInputSchema>;

/** Nova ordem das perspectivas ativas do projeto (todas, da primeira à última). */
export const perspectiveOrderSchema = z.object({
  ids: z
    .array(z.string().uuid())
    .min(1)
    .max(30)
    .refine((ids) => new Set(ids).size === ids.length, 'Perspectiva repetida na ordem.'),
});

/** Soma dos pesos das perspectivas ativas (para o aviso de "a soma deveria ser 100%"). */
export function perspectiveWeightTotal(items: Pick<Perspective, 'weight' | 'archivedAt'>[]): number {
  const total = items.filter((p) => !p.archivedAt).reduce((sum, p) => sum + (p.weight ?? 0), 0);
  return Math.round(total * 100) / 100;
}

/** Projeto no espaço de trabalho: o arquivado continua aberto para consulta (só leitura). */
export const workspaceProjectSchema = projectSummarySchema.extend({ status: z.enum(PROJECT_STATUSES) });
export type WorkspaceProject = z.infer<typeof workspaceProjectSchema>;

/** Espaço de trabalho de uma empresa: dados básicos e os projetos BSC (em andamento primeiro). */
export const workspaceSchema = z.object({
  organization: z.object({ id: z.string().uuid(), name: z.string(), isFluxus: z.boolean() }),
  projects: z.array(workspaceProjectSchema),
});
export type Workspace = z.infer<typeof workspaceSchema>;
